import { type ApolloClient, gql } from '@apollo/client/core';
import type {
  DeleteTeamRoleMutationInput,
  Mutation,
  Query,
  SaveTeamRoleMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as TeamRole from './TeamRole';
import * as TeamRoleDetails from './TeamRoleDetails';
import * as TeamRoleInput from './TeamRoleInput';
import { TEAM_ROLE_DETAILS_FIELDS, TEAM_ROLE_FIELDS } from './fragments';

/**
 * Team-role administration on the tenant (AppSchema) connection. The same four fields exist on
 * the legacy schema nested under `app { … }`, `@deprecated … Removed in 6.1`; see
 * `doc/claude/api-layer.md` for the three contract changes the move brings.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcomes — an absent payload, a populated
 * `errors` list. That matches `App/Event` and `App/Attachment` and preserves the legacy store's
 * `try`/`finally`-with-no-`catch`, and it deliberately diverges from `App/Genre`, which folds a
 * thrown error into a `VPResult`. This is a transport move, not a change of failure semantics.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

export const TEAM_ROLES_QUERY = gql`
  query teamRoles {
    teamRoles {
      ...TeamRoleFields
    }
  }
  ${TEAM_ROLE_FIELDS}
`;

export const TEAM_ROLE_DETAILS_QUERY = gql`
  query teamRoleDetails($roleId: Int!) {
    teamRoleDetails(roleId: $roleId) {
      ...TeamRoleDetailsFields
    }
  }
  ${TEAM_ROLE_DETAILS_FIELDS}
`;

export const SAVE_TEAM_ROLE_MUTATION = gql`
  mutation saveTeamRole($input: SaveTeamRoleMutationInput!) {
    saveTeamRole(input: $input) {
      teamRole {
        ...TeamRoleDetailsFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TEAM_ROLE_DETAILS_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_TEAM_ROLE_MUTATION = gql`
  mutation deleteTeamRole($input: DeleteTeamRoleMutationInput!) {
    deleteTeamRole(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * The roles of the account the connection is scoped to — by the `X-VP-Account-Id` header, since
 * the AppSchema field takes no `accountId`. `teamRoles` is `[TeamRole!]!`, so an absent list is a
 * backend fault: resolving it as an empty one would render "no roles" over a failed read.
 */
export const fetchTeamRoles = async (
  client: ApolloClient<Query>,
): Promise<VPResult<TeamRole.TeamRole[], VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: TEAM_ROLES_QUERY,
    fetchPolicy: 'network-only',
  });

  if (!data?.teamRoles) {
    return failure('Failed to fetch the team roles.');
  }

  return { success: true, data: data.teamRoles.map(TeamRole.fromGql) };
};

/**
 * `network-only` because the editor has to show what the server currently holds: a cached read
 * would re-render stale permissions when a role is reopened after an edit.
 */
export const fetchTeamRoleDetails = async (
  client: ApolloClient<Query>,
  roleId: number,
): Promise<VPResult<TeamRoleDetails.TeamRoleDetails, VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: TEAM_ROLE_DETAILS_QUERY,
    variables: { roleId },
    fetchPolicy: 'network-only',
  });

  if (!data?.teamRoleDetails) {
    return failure('Failed to fetch the team role.');
  }

  return { success: true, data: TeamRoleDetails.fromGql(data.teamRoleDetails) };
};

/**
 * Creates when the input carries no `id`, updates when it does.
 */
export const saveTeamRole = async (
  client: ApolloClient<Mutation>,
  teamRole: TeamRoleInput.TeamRoleInput,
): Promise<VPResult<TeamRoleDetails.TeamRoleDetails, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the generic does not check `variables`, and the argument names
  // `teamRole` / `teamRoleId` are exactly what this migration renamed.
  const input: SaveTeamRoleMutationInput = { teamRole: TeamRoleInput.toGqlInput(teamRole) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_TEAM_ROLE_MUTATION,
    variables: { input },
  });

  const payload = data?.saveTeamRole;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // No `success` on this payload, so an empty error list with no entity is the one shape that
  // would otherwise read as a silent success.
  if (!payload?.teamRole) {
    return failure('Failed to save the team role.');
  }

  return { success: true, data: TeamRoleDetails.fromGql(payload.teamRole) };
};

export const deleteTeamRole = async (
  client: ApolloClient<Mutation>,
  teamRoleId: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const input: DeleteTeamRoleMutationInput = { teamRoleId };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_TEAM_ROLE_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteTeamRole;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A failure the caller cannot render is worse than a generic message.
  return errors.length ? { success: false, errors } : failure('Failed to delete the team role.');
};
